'use client';

import Image from 'next/image';

/** Display size for 2x assets: half of intrinsic (919x1024) → 460x512 */
const FEATURE_CARD_DISPLAY_WIDTH = 460;
const FEATURE_CARD_DISPLAY_HEIGHT = 512;

/** In-card preview for 4th feature: dispute recovery email */
const RecoveryEmailPreview = () => {
  return (
    <div className="relative group max-w-lg">
      <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
        {/* Email Header Bar */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              New Message
            </span>
            <div className="flex space-x-2">
              <div className="w-2 h-2 rounded-full bg-gray-300" />
              <div className="w-2 h-2 rounded-full bg-gray-300" />
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex border-b border-gray-100 pb-2">
              <span className="text-gray-400 w-12">To:</span>
              <span className="text-gray-900 font-medium">
                SH_Commissions_Administration@uhc.com
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-12">Subj:</span>
              <span className="text-gray-900 font-medium italic">
                Dispute: Missing Commissions - Dan Alders
              </span>
            </div>
          </div>
        </div>
        {/* Email Body */}
        <div className="p-8 bg-white text-gray-800 text-sm leading-relaxed">
          <p className="mb-4">To Whom It May Concern,</p>
          <p className="mb-4">
            Our automated audit has identified a
            <span className="text-orange-600 font-bold bg-orange-50 px-1 rounded">
              {' '}
              $4,120.60 discrepancy{' '}
            </span>
            in the recent settlement for Agent Dan Alders.
          </p>
          <div className="bg-gray-50 border-l-4 border-green-500 p-4 my-6">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">
              Audit Summary:
            </p>
            <ul className="space-y-1">
              <li>
                • Expected Renewals:{' '}
                <span className="font-bold text-gray-900">$12,000.00</span>
              </li>
              <li>
                • Integrity Gap:{' '}
                <span className="font-bold text-orange-600">
                  8 Policies (6 MAPD, 2 T65)
                </span>
              </li>
            </ul>
          </div>
          <p className="font-bold text-green-700">
            Please review the attached audit logs and reconcile this gap within 10 business
            days.
          </p>
          <div className="mt-8 pt-6 border-t border-gray-100 text-gray-400 italic text-xs">
            Generated via Tally Advanced Audit System.
          </div>
        </div>
      </div>
    </div>
  );
};

const features = [
  {
    label: 'A clean commission source of truth',
    title:
      'Tally replaces hours of manual data entry with a one-click audit that reconciles your carrier statements against your actual bank deposits.',
    body: '',
    imageSrc: '/feature-card-1.png',
    imageAlt: 'Total Expected Revenue and Policies Sold by Carrier',
    imageWidth: FEATURE_CARD_DISPLAY_WIDTH,
    imageHeight: FEATURE_CARD_DISPLAY_HEIGHT,
  },
  {
    label: 'Your entire book of business, audited automatically',
    title:
      'Know exactly what your day was worth. Your Book of Business finally organized by revenue.',
    body: '',
    imageSrc: '/feature-card-2.png',
    imageAlt: 'Revenue Health donut chart showing payout status breakdown',
    imageWidth: FEATURE_CARD_DISPLAY_WIDTH,
    imageHeight: FEATURE_CARD_DISPLAY_HEIGHT,
  },
  {
    label: 'Every dollar protected',
    title:
      "No more manual cross-referencing. Tally's algorithm verify every member, plan code, and payment date in real-time.",
    body: '',
    imageSrc: '/feature-card-3.png',
    imageAlt: 'Daily Feed: policies sold, appointments, MAPD renewals, T65 business',
    imageWidth: FEATURE_CARD_DISPLAY_WIDTH,
    imageHeight: FEATURE_CARD_DISPLAY_HEIGHT,
  },
  {
    label: 'Dispute recovery',
    title: '1-Click Dispute Recovery',
    body:
      'No more wasting hours on the phone with carriers; Tally instantly generates your dispute-ready recovery emails so you can get paid without the back-and-forth.',
    imageSrc: '',
    imageAlt: '',
    imageWidth: FEATURE_CARD_DISPLAY_WIDTH,
    imageHeight: FEATURE_CARD_DISPLAY_HEIGHT,
    customImage: true,
  },
];

export default function MarketingFeatureStrip() {
  return (
    <section id="features" className="mt-16 md:mt-20 pt-0">
      {/* Base layer: sticky on desktop; simple static heading on mobile for smoother scroll */}
      <div className="block sm:hidden w-full px-4 pt-6 pb-2 text-center">
        <p className="font-poppins text-[24px] !font-bold tracking-tight leading-[1.25] text-[#0f172a]">
          Find missing earnings in seconds without switching your CRM.
        </p>
      </div>
      <div className="hidden sm:flex sticky top-0 z-0 h-[45vh] min-h-[200px] w-full flex-col items-center justify-center px-4 text-center">
        <p className="font-poppins text-[28px] md:text-[40px] font-semibold tracking-tight leading-[1.25] text-[#0f172a] max-w-3xl -mb-[40px]">
          Find missing earnings in seconds without switching your CRM.
        </p>
      </div>
      {/* Cards: sticky on desktop only; normal stacked cards on mobile for better scroll feel */}
      <div className="relative z-10 pt-1">
        {features.map((feature, index) => (
          <div
            key={feature.title}
            className="w-full flex flex-col items-center px-0 sm:px-4 py-10 sm:py-0 sm:sticky sm:top-0 sm:h-[80vh] sm:min-h-[520px] sm:justify-center"
            style={{
              background: index === 0
                ? 'linear-gradient(0deg, #fff9f3 0%, #fff9f3 20%, #b8e6c8 50%, #fff9f3 100%)'
                : index === 1
                  ? 'linear-gradient(170deg, #fff9f3 0%, #fff9f3 50%, #e6caff 90%, #fff9f3 100%)'
                  : index === 2
                    ? 'linear-gradient(170deg, #fff9f3 0%, #fff9f3 22%, #ffddbb 42%, #fff9f3 88%, #fff9f3 100%)'
                    : 'linear-gradient(170deg, #fff9f3 0%, #fff9f3 50%, #b1d6f6 55%, #fff9f3 80%, #fff9f3 100%)',
            }}
          >
            <div className="w-full max-w-[65rem] min-h-[320px] mt-[22px] md:min-h-0 md:max-h-[512px]">
              <div className="bg-transparent sm:bg-white overflow-visible sm:overflow-hidden flex flex-col sm:shadow-[-7px_7px_22px_-4px_rgba(148,163,184,0.35)]">
                {/* Mobile: image first, then copy below. Desktop: copy left, image right; all cards = exact image height */}
                <div className="grid grid-cols-1 md:grid-cols-[6fr_4fr] gap-0 md:items-stretch md:h-[512px]">
                  {/* Copy: below image on mobile (order-2), left column on desktop (order-1) */}
                  <div className="pt-6 pb-5 px-6 sm:pt-10 sm:px-8 sm:pb-8 md:p-[4.5rem] flex flex-col justify-start bg-transparent sm:bg-white text-center md:text-left order-2 md:order-1 min-h-0 overflow-hidden">
                    <div className="space-y-3.5 flex-1 min-h-0">
                      <p className="hidden md:block text-[10px] font-semibold tracking-[0.12em] uppercase text-[#00b85a] mb-4">
                        {feature.label}
                      </p>
                      <h3 className="font-poppins text-[18px] sm:text-[22px] md:text-[26px] font-semibold text-slate-900 leading-[1.25]">
                        {feature.title}
                      </h3>
                      <p className="text-[11px] md:text-[12px] text-slate-600 leading-relaxed">
                        {feature.body}
                      </p>
                    </div>
                    <div className="mt-5 p-3.5 shrink-0 flex justify-center md:justify-start">
                      <a
                        href="/public-subscription-selection"
                        className="inline-flex justify-center px-[61px] py-3.5 rounded-md bg-[#236434] text-white font-semibold text-base hover:bg-[#1c522e] transition-colors w-full md:w-auto"
                      >
                        Get early access
                      </a>
                    </div>
                  </div>
                  {/* Image or custom component: first on mobile (order-1), right column on desktop (order-2) */}
                  <div className="relative w-full flex flex-col items-stretch bg-transparent sm:bg-white order-1 md:order-2 overflow-visible md:overflow-hidden rounded-none">
                    {'customImage' in feature && feature.customImage ? (
                      <div className="relative w-full md:w-full md:h-[512px] flex items-center justify-center bg-slate-900/5">
                        <div className="w-full">
                          <RecoveryEmailPreview />
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-full md:w-full md:h-[512px] flex items-center justify-center">
                        <div
                          className="relative w-full overflow-hidden rounded-none md:w-full md:h-[512px]"
                          style={{ aspectRatio: `${feature.imageWidth / feature.imageHeight}` }}
                        >
                          <Image
                            src={feature.imageSrc}
                            alt={feature.imageAlt}
                            fill
                            className="object-cover object-center rounded-none scale-[1.02] sm:scale-100"
                            sizes="(max-width: 768px) 100vw, 460px"
                            quality={95}
                            priority={index === 0}
                          />
                        </div>
                      </div>
                    )}
                    {/* Spacer on mobile (button lives in copy block below); desktop has no extra footer here */}
                    <div className="mt-3 w-full px-2 sm:px-4 pb-3 sm:pb-8 md:hidden md:mt-0 md:pb-0" aria-hidden />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
